import { type Graph, type GraphEdge, type GraphNode } from '../models/graph';
import { type Level } from '../models/level';
import { WORLDS, worldById, type World } from '../models/world';
import { type MapScene } from './cityMap';
import {
  expandRect,
  graphBounds,
  LANDMARK_GROUP,
  planCity,
  type Landmark,
  type Point,
  type Rect,
} from './cityPlan';
import { type SpriteCatalog } from './sprites';
import { hashString, mulberry32 } from './rng';

const DISTRICT_SITES: readonly { readonly x: number; readonly y: number }[] = [
  { x: 0.12, y: 0.78 },
  { x: 0.3, y: 0.4 },
  { x: 0.46, y: 0.72 },
  { x: 0.58, y: 0.34 },
  { x: 0.74, y: 0.6 },
  { x: 0.88, y: 0.82 },
  { x: 0.9, y: 0.18 },
];

const DISTRICT_STREETS: readonly (readonly [number, number])[] = [
  [1, 2],
  [2, 3],
  [3, 4],
  [4, 5],
  [5, 6],
  [6, 7],
  [1, 3],
  [2, 4],
  [3, 5],
  [4, 7],
];

const GEARFALL_RIVER: readonly Point[] = [
  { x: -0.08, y: 0.7 },
  { x: 0.12, y: 0.66 },
  { x: 0.3, y: 0.64 },
  { x: 0.46, y: 0.6 },
  { x: 0.6, y: 0.52 },
  { x: 0.74, y: 0.5 },
  { x: 0.86, y: 0.62 },
  { x: 0.95, y: 0.75 },
  { x: 1.08, y: 0.85 },
];

const OVERVIEW_SIZE_SCALE = 0.55;
const DISTRICT_HALF_SIZE = 0.15;
const RUN_JITTER = 0.035;
const RUN_MARGIN_X = 0.08;
const RUN_MARGIN_Y = 0.14;
const RUN_CELL_OVERLAP = 0.75;
const RIVER_REACH = 0.35;

export function worldPinId(worldId: number): string {
  return `w${worldId}`;
}

export function runPinId(levelIndex: number): string {
  return `l${levelIndex}`;
}

function edge(id: string, a: string, b: string): GraphEdge {
  return { id, sourceId: a, targetId: b, weight: 1, capacity: 0 };
}

export function gearfallMap(): MapScene {
  const nodes: GraphNode[] = WORLDS.map((world, i) => ({
    id: worldPinId(world.id),
    label: world.name,
    position: DISTRICT_SITES[i] ?? { x: 0.5, y: 0.5 },
    type: 'normal',
  }));
  const edges = DISTRICT_STREETS.map(([a, b]) => edge(`s${a}-${b}`, worldPinId(a), worldPinId(b)));
  const graph: Graph = { nodes, edges, directed: false };
  return { id: 'map:gearfall', graph, sizeScale: OVERVIEW_SIZE_SCALE, canalPath: GEARFALL_RIVER };
}

function runGrid(total: number): { cols: number; rows: number } {
  const cols = total > 8 ? 5 : 4;
  return { cols, rows: Math.ceil(total / cols) };
}

function runSpacing(total: number): Point {
  const { cols, rows } = runGrid(total);
  return {
    x: cols > 1 ? (1 - RUN_MARGIN_X * 2) / (cols - 1) : 1,
    y: rows > 1 ? (1 - RUN_MARGIN_Y * 2) / (rows - 1) : 1,
  };
}

function cellAround(center: Point, halfX: number, halfY: number): Rect {
  return {
    minX: center.x - halfX,
    minY: center.y - halfY,
    maxX: center.x + halfX,
    maxY: center.y + halfY,
  };
}

function mapPoint(p: Point, from: Rect, to: Rect): Point {
  const sx = (to.maxX - to.minX) / (from.maxX - from.minX);
  const sy = (to.maxY - to.minY) / (from.maxY - from.minY);
  return { x: to.minX + (p.x - from.minX) * sx, y: to.minY + (p.y - from.minY) * sy };
}

function segmentMeetsRect(a: Point, b: Point, r: Rect): boolean {
  let t0 = 0;
  let t1 = 1;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0;
    const t = q / p;
    if (p < 0) {
      if (t > t1) return false;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return false;
      if (t < t1) t1 = t;
    }
    return true;
  };
  return (
    clip(-dx, a.x - r.minX) &&
    clip(dx, r.maxX - a.x) &&
    clip(-dy, a.y - r.minY) &&
    clip(dy, r.maxY - a.y)
  );
}

function courseThrough(
  course: readonly Point[],
  from: Rect,
  to: Rect,
  reach: Rect,
): Point[] | null {
  const mapped = course.map((p) => mapPoint(p, from, to));
  const visible = mapped.some((p, i) => i > 0 && segmentMeetsRect(mapped[i - 1]!, p, reach));
  return visible ? mapped : null;
}

function inside(p: Point, r: Rect): boolean {
  return p.x >= r.minX && p.x <= r.maxX && p.y >= r.minY && p.y <= r.maxY;
}

function landmarksWithin(
  parent: MapScene,
  catalog: SpriteCatalog,
  cell: Rect,
  bounds: Rect,
): Landmark[] {
  const plan = planCity(parent, {
    catalog,
    ...(parent.sizeScale === undefined ? {} : { sizeScale: parent.sizeScale }),
    ...(parent.canalPath === undefined ? {} : { canalPath: parent.canalPath }),
    ...(parent.landmarksFor ? { landmarks: parent.landmarksFor(catalog) } : {}),
  });
  return plan.buildings
    .filter((b) => b.group === LANDMARK_GROUP && b.spriteId !== null && inside(b.anchor, cell))
    .map((b) => ({ spriteId: b.spriteId!, anchor: mapPoint(b.anchor, cell, bounds) }));
}

function districtCell(world: World): Rect | null {
  const site = DISTRICT_SITES[world.id - 1];
  return site ? cellAround(site, DISTRICT_HALF_SIZE, DISTRICT_HALF_SIZE) : null;
}

function runCell(world: World, levelIndex: number): Rect | null {
  const pin = districtMap(world).graph.nodes.find((n) => n.id === runPinId(levelIndex));
  if (!pin) return null;
  const spacing = runSpacing(world.totalLevels);
  return cellAround(pin.position, spacing.x * RUN_CELL_OVERLAP, spacing.y * RUN_CELL_OVERLAP);
}

export function districtLandmarks(world: World, catalog: SpriteCatalog): Landmark[] {
  const cell = districtCell(world);
  if (!cell) return [];
  return landmarksWithin(gearfallMap(), catalog, cell, graphBounds(districtMap(world).graph));
}

export function levelLandmarks(
  level: Pick<Level, 'worldIndex' | 'levelIndex' | 'graph'>,
  catalog: SpriteCatalog,
): Landmark[] {
  const world = worldById(level.worldIndex);
  const cell = world ? runCell(world, level.levelIndex) : null;
  if (!world || !cell) return [];
  return landmarksWithin(districtScene(world), catalog, cell, graphBounds(level.graph));
}

export function districtRiver(world: World): Point[] | null {
  const cell = districtCell(world);
  if (!cell) return null;
  const bounds = graphBounds(districtMap(world).graph);
  return courseThrough(GEARFALL_RIVER, cell, bounds, expandRect(bounds, RIVER_REACH));
}

export function levelRiver(
  level: Pick<Level, 'worldIndex' | 'levelIndex' | 'graph'>,
): Point[] | null {
  const world = worldById(level.worldIndex);
  const river = world ? districtRiver(world) : null;
  const cell = world ? runCell(world, level.levelIndex) : null;
  if (!river || !cell) return null;

  const bounds = graphBounds(level.graph);
  return courseThrough(river, cell, bounds, expandRect(bounds, RIVER_REACH));
}

// A generated level has no run on its district map, so it borrows the district's
// water as a canal of its own (undefined) instead of inheriting a course.
export function boardCanal(
  level: Pick<Level, 'worldIndex' | 'levelIndex' | 'graph'>,
): readonly Point[] | null | undefined {
  const river = levelRiver(level);
  if (river) return river;
  const world = worldById(level.worldIndex);
  const generated = world !== undefined && runCell(world, level.levelIndex) === null;
  return generated && districtRiver(world) !== null ? undefined : null;
}

export function districtMap(world: World): MapScene {
  const total = world.totalLevels;
  const { cols, rows } = runGrid(total);
  const random = mulberry32(hashString(`district:${world.id}`));
  const jitter = (): number => (random() * 2 - 1) * RUN_JITTER;

  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const idAt = new Map<string, string>();

  for (let index = 1; index <= total; index++) {
    const row = Math.floor((index - 1) / cols);
    const step = (index - 1) % cols;
    const col = row % 2 === 0 ? step : cols - 1 - step;
    const x = cols > 1 ? RUN_MARGIN_X + (col / (cols - 1)) * (1 - RUN_MARGIN_X * 2) : 0.5;
    const y = rows > 1 ? RUN_MARGIN_Y + (row / (rows - 1)) * (1 - RUN_MARGIN_Y * 2) : 0.5;
    const id = runPinId(index);
    nodes.push({
      id,
      label: String(index),
      position: { x: x + jitter(), y: y + jitter() },
      type: 'normal',
    });
    idAt.set(`${row},${col}`, id);
    if (index > 1) edges.push(edge(`r${index - 1}-${index}`, runPinId(index - 1), id));
  }

  for (let row = 1; row < rows; row++) {
    for (let col = 0; col < cols; col += 2) {
      const above = idAt.get(`${row - 1},${col}`);
      const here = idAt.get(`${row},${col}`);
      if (!above || !here) continue;
      const linked = edges.some(
        (e) =>
          (e.sourceId === above && e.targetId === here) ||
          (e.sourceId === here && e.targetId === above),
      );
      if (!linked) edges.push(edge(`b${row}-${col}`, above, here));
    }
  }

  const graph: Graph = { nodes, edges, directed: false };
  return { id: `map:district-${world.id}`, graph };
}

export function districtScene(world: World): MapScene {
  return {
    ...districtMap(world),
    canalPath: districtRiver(world),
    landmarksFor: (catalog) => districtLandmarks(world, catalog),
  };
}
